<template>
  <!-- "Ocupar aplicaciones disponibles" entry for the Pruebas Informales chatbot.
       Unified vitrina (download / correct / class material) with internal views
       for report feedback. -->
  <div class="mas-root">
  <div class="mas" :class="{ 'mas--flow': noInnerScroll }">
    <div v-if="view === 'hub'" class="mas-hub">
      <InformalTestsVitrina
        :active-mode="activeMode"
        :class-material-only="classMaterialOnly"
        :grades="grades"
        :subjects="subjects"
        :materials="tests"
        :selected-grade="selectedGrade"
        :selected-subject="selectedSubject"
        :selected-material="selectedTest"
        :loading-grades="loadingGrades"
        :loading-subjects="loadingSubjects"
        :loading-materials="loadingTests"
        @mode-change="onModeChange"
        @grade-change="onGradeChange"
        @subject-change="onSubjectChange"
        @material-change="onMaterialChange"
        @download="downloadSelected"
        @no-uses="$emit('no-uses')"
      >
        <template #toolbar-extra>
          <slot name="toolbar-extra" />
        </template>
        <template #correct>
          <p v-if="requireStudent && !hostStudentId" class="mas-prompt">
            Selecciona un estudiante para comenzar la corrección.
          </p>
          <MaterialScoreInput
            v-else-if="selectedTest"
            ref="scoreInput"
            :key="scoreInputKey"
            :context="context"
            :can-work="canWork"
            @go-to-step="onScoreEvent"
            @await-report="$emit('await-report', $event)"
            @review-another="$emit('review-another')"
          />
          <p v-else class="mas-muted">Selecciona una prueba para corregir.</p>
        </template>
        <template #class-material>
          <MaterialAiGenerate
            ref="aiGenerate"
            :context="context"
            :can-work="canWork"
            embedded-in-vitrina
            @material-done="showAiFeedback = true"
            @go-to-step="onFinalEvent"
          />
        </template>
      </InformalTestsVitrina>
    </div>

    <!-- DONE (report generated + feedback) — also embedded. -->
    <div v-else-if="view === 'done'">
      <button type="button" class="mas-back" @click="view = 'hub'">
        ← Volver
      </button>
      <MaterialFinalStep
        :context="context"
        :can-work="canWork"
        @go-to-step="onFinalEvent"
      />
    </div>
  </div>

  <!-- Feedback AI material: fuera del card rosa, entre componente y chatbot. -->
  <MaterialFinalStep
    v-if="showAiFeedback && view === 'hub' && activeMode === 'class-material'"
    class="mas-ai-feedback"
    :context="context"
    :can-work="canWork"
    finish-label="Empezar de nuevo"
    finish-step="restart-ai"
    @go-to-step="onAiFeedbackEvent"
  />
  </div>
</template>

<script>
import customMaterialService from "@/services/customMaterialService";
import customTestService from "@/services/customTestService";
import informationService from "@/services/informationService";
import MaterialScoreInput from "./MaterialScoreInput.vue";
import MaterialFinalStep from "./MaterialFinalStep.vue";
import MaterialAiGenerate from "./MaterialAiGenerate.vue";
import InformalTestsVitrina from "./InformalTestsVitrina.vue";
import {
  DEFAULT_GRADE,
  DEFAULT_SUBJECT,
  SPECIAL_SUBJECTS,
  pickDefaultMaterial,
} from "./informalTestsDefaults";

export default {
  name: "MaterialApplicationStep",
  components: {
    MaterialScoreInput,
    MaterialFinalStep,
    MaterialAiGenerate,
    InformalTestsVitrina,
  },
  props: {
    context: { type: Object, required: false, default: () => ({}) },
    canWork: { type: Boolean, required: false, default: false },
    // Host (redpie) opt-out of the fixed-height inner scroll: let the component
    // grow to whatever height it needs.
    noInnerScroll: { type: Boolean, required: false, default: false },
    // Host (redpie): block the score sheet until a student is chosen.
    requireStudent: { type: Boolean, required: false, default: false },
    // Product shells (e.g. Materiales para la clase) can open directly on IA.
    initialMode: {
      type: String,
      required: false,
      default: "download",
      validator: (v) => ["download", "correct", "class-material"].includes(v),
    },
    classMaterialOnly: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      view: "hub",
      activeMode: this.initialMode || "download",
      showAiFeedback: false,
      grades: [],
      loadingGrades: false,
      selectedGrade: null,
      subjects: [],
      loadingSubjects: false,
      selectedSubject: null,
      tests: [],
      loadingTests: false,
      selectedTest: null,
      scoreInputKey: 0,
    };
  },
  computed: {
    // Student chosen by a host (redpie informal-tests), passed via `context`.
    hostStudentId() {
      return this.context && this.context.studentGradeId
        ? this.context.studentGradeId
        : null;
    },
  },
  watch: {
    // When the host (redpie) switches student, clear the score sheet in place
    // for the next student. The selected material is untouched.
    hostStudentId() {
      if (this.$refs.scoreInput && this.$refs.scoreInput.reset) {
        this.$refs.scoreInput.reset();
      }
    },
  },
  mounted() {
    this.initHub();
  },
  methods: {
    initHub() {
      this.fetchGrades().then(() => {
        this.selectedGrade =
          this.grades.find((g) => DEFAULT_GRADE.test(g.original_name)) || null;
        if (!this.selectedGrade) return null;
        return this.fetchSubjects().then(() => {
          this.selectedSubject =
            this.subjects.find((s) => DEFAULT_SUBJECT.test(s.name)) || null;
          if (this.selectedSubject) return this.fetchTests();
        });
      });
    },
    fetchGrades() {
      this.loadingGrades = true;
      this.grades = [];
      return informationService.getPieGrades().then(
        (data) => {
          this.grades = ((data && data.grades) || [])
            .filter((g) => ![14, 15, 16].includes(g[0]))
            .map((g) => ({ code: g[0], original_name: g[1] }));
          this.loadingGrades = false;
        },
        () => {
          this.grades = [];
          this.loadingGrades = false;
        }
      );
    },
    fetchSubjects() {
      this.loadingSubjects = true;
      this.subjects = [];
      return customTestService
        .getSubjectHashtags(this.selectedGrade.original_name)
        .then(
          (data) => {
            this.subjects = (data || []).filter((s) =>
              SPECIAL_SUBJECTS.includes(s.name)
            );
            this.loadingSubjects = false;
          },
          () => {
            this.subjects = [];
            this.loadingSubjects = false;
          }
        );
    },
    fetchTests() {
      this.selectedTest = null;
      this.loadingTests = true;
      this.tests = [];
      const school = this.$store.state.login.school;
      return customMaterialService
        .getFilteredMaterials(
          school,
          this.selectedSubject.enum_code,
          this.selectedGrade.code
        )
        .then(
          (data) => {
            this.tests = (data && data.materials) || [];
            this.selectedTest = pickDefaultMaterial(
              this.tests,
              this.selectedSubject.name
            );
            this.syncCorrectionStore();
            this.loadingTests = false;
          },
          () => {
            this.tests = [];
            this.loadingTests = false;
          }
        );
    },
    syncCorrectionStore() {
      if (!this.selectedTest || !this.selectedGrade) return;
      const commit = (name, key, value) =>
        this.$store.commit("chatbot/updateMessage", { name, key, value });
      commit("existing_materials_list", "selectedMaterialId", this.selectedTest.id);
      commit("existing_materials_list", "selectedMaterialName", this.selectedTest.name);
      commit(
        "existing_materials_list",
        "selectedMaterialPdf",
        this.selectedTest.pdf || null
      );
      commit("grade", "answer", {
        code: this.selectedGrade.code,
        original_name: this.selectedGrade.original_name,
      });
      this.scoreInputKey += 1;
    },
    onGradeChange(grade) {
      this.selectedGrade = grade;
      this.selectedSubject = null;
      this.subjects = [];
      this.tests = [];
      this.selectedTest = null;
      if (this.selectedGrade) this.fetchSubjects();
    },
    onSubjectChange(subject) {
      this.selectedSubject = subject;
      this.tests = [];
      this.selectedTest = null;
      if (this.selectedSubject) this.fetchTests();
    },
    onMaterialChange(material) {
      if (!material || !material.pdf) return;
      this.selectedTest = material;
      this.syncCorrectionStore();
    },
    downloadSelected(material) {
      const test = material || this.selectedTest;
      if (test && test.pdf) {
        window.open(test.pdf, "_blank", "noopener");
      }
    },
    onModeChange(mode) {
      this.activeMode = mode;
      if (mode !== "class-material") this.showAiFeedback = false;
    },
    onScoreEvent(step) {
      if (step === "report_done") this.view = "done";
      else this.$emit("go-to-step", step);
    },
    onFinalEvent(step) {
      this.$emit("go-to-step", step);
    },
    onAiFeedbackEvent(step) {
      this.showAiFeedback = false;
      // Local restart for Materiales IA — does not use shared chat reset
      // (ChatbotMaterialesClase noops go-to-step; other shells remount).
      if (step === "restart-ai") {
        const ai = this.$refs.aiGenerate;
        if (ai && typeof ai.restartToPick === "function") {
          ai.restartToPick();
        }
        return;
      }
      this.$emit("go-to-step", step);
    },
  },
};
</script>

<style scoped>
.mas-root {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.mas {
  --c: var(--main-color, #e84855);
  --c-soft: var(--product-soft, var(--secondary-color, #fbe1e4));
  font-family: "Ubuntu", Arial, sans-serif;
  border: 2px solid var(--c);
  border-radius: 16px;
  background: #fff;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-top-width: 5px;
}
.mas-ai-feedback {
  flex-shrink: 0;
  width: 100%;
  padding: 14px 16px 16px;
  border: 1px dashed rgba(232, 72, 85, 0.28);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(26, 35, 50, 0.05);
}

@media (min-width: 768px) {
  .mas {
    /* Tall shell: botonera + ~8 OAs/indicadores + footer caben sin colapsar. */
    height: min(92vh, 980px);
    max-height: min(92vh, 980px);
    min-height: 780px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  /* Host opt-out: grow to full content height, no inner scroll. */
  .mas.mas--flow {
    height: auto;
    max-height: none;
    min-height: 0;
    overflow: visible;
  }
}
.mas-hub {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 575.98px) {
  .mas {
    padding: 14px;
    border-radius: 14px;
  }
}

.mas ::v-deep .msi-questions {
  max-height: none;
  overflow-y: visible;
  padding-right: 0;
}

.mas-muted {
  font-size: 0.84rem;
  color: #9aa3af;
  margin: 8px 0;
}

.mas-prompt {
  font-size: 1rem;
  font-weight: 800;
  color: #000;
  margin: 12px 0;
}

.mas-back {
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  padding: 6px 0 10px;
}
.mas-back:hover {
  color: var(--c);
}
</style>
